import { useState, useEffect } from "react";
import { Dialog, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { useCreateMacDrive, useUpdateMacDrive } from "@/api/devices";
import type { MacDeviceDrive } from "@/api/types";

interface Props {
  open: boolean;
  onClose: () => void;
  tenantId: string;
  deviceId: string;
  drive?: MacDeviceDrive | null;
}

export function MacDriveModal({ open, onClose, tenantId, deviceId, drive }: Props) {
  const isEdit = !!drive;
  const create = useCreateMacDrive(tenantId, deviceId);
  const update = useUpdateMacDrive(tenantId, deviceId);
  const isPending = create.isPending || update.isPending;

  const [name, setName] = useState("");
  const [encrypted, setEncrypted] = useState(false);
  const [filevaultEnabled, setFilevaultEnabled] = useState(false);
  const [isBootVolume, setIsBootVolume] = useState(false);

  useEffect(() => {
    if (open) {
      setName(drive?.name ?? "");
      setEncrypted(drive?.encrypted ?? false);
      setFilevaultEnabled(drive?.filevault_enabled ?? false);
      setIsBootVolume(drive?.is_boot_volume ?? false);
    }
  }, [open, drive]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = {
      name,
      encrypted,
      filevault_enabled: filevaultEnabled,
      is_boot_volume: isBootVolume,
    };
    const onSuccess = { onSuccess: onClose };
    if (isEdit) {
      update.mutate({ id: drive.id, payload }, onSuccess);
    } else {
      create.mutate({ payload }, onSuccess);
    }
  };

  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>{isEdit ? "Edit" : "Add"} Drive</DialogTitle>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="mb-1.5 block text-[11px] font-medium uppercase tracking-wider text-text-muted">Name *</label>
          <Input value={name} onChange={e => setName(e.target.value)} required placeholder="/" />
        </div>
        <div className="flex items-center justify-between">
          <label className="text-sm text-text-muted">Encrypted</label>
          <Switch checked={encrypted} onChange={setEncrypted} />
        </div>
        <div className="flex items-center justify-between">
          <label className="text-sm text-text-muted">FileVault Enabled</label>
          <Switch checked={filevaultEnabled} onChange={setFilevaultEnabled} />
        </div>
        <div className="flex items-center justify-between">
          <label className="text-sm text-text-muted">Boot Volume</label>
          <Switch checked={isBootVolume} onChange={setIsBootVolume} />
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={isPending}>Cancel</Button>
          <Button type="submit" disabled={isPending || !name}>{isPending ? "Saving..." : isEdit ? "Update" : "Add"}</Button>
        </div>
      </form>
    </Dialog>
  );
}
